import { useState, useCallback } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from '../services/onboardingApi';
import { OnboardingProject } from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Settings,
  Database,
  Users,
  Mail,
  Download,
  Play,
  Globe,
  Bot,
  Building,
  CreditCard,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useReviewData, generateReviewReport, generateReviewCSV, ChecklistItem } from '../hooks/useReviewData';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

const Stage5Review = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, loadProject } = useOutletContext<OutletContext>();

  const { summary, checklist, isLoading } = useReviewData(projectId);
  const [testResults, setTestResults] = useState<Record<string, any>>({});
  const [runningTests, setRunningTests] = useState<Record<string, boolean>>({});

  const requiredItemsComplete = checklist
    .filter((item) => item.required)
    .every((item) => item.status === 'complete');

  const allItemsComplete = checklist.every((item) => item.status === 'complete' || !item.required);

  const runTest = useCallback(async (testId: string) => {
    setRunningTests(prev => ({ ...prev, [testId]: true }));
    try {
      toast({
        title: 'Running Test',
        description: `Executing ${testId.replace('_', ' ')} test...`,
      });

      // Simulate test execution (replace with real Edge Function call)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setTestResults(prev => ({
        ...prev,
        [testId]: {
          status: 'passed',
          message: 'Test passed successfully',
          timestamp: new Date().toISOString(),
        },
      }));

      toast({
        title: 'Test Passed',
        description: `${testId.replace('_', ' ')} test completed successfully`,
      });
    } catch (error: any) {
      setTestResults(prev => ({
        ...prev,
        [testId]: {
          status: 'failed',
          message: error.message || 'Test failed',
          timestamp: new Date().toISOString(),
        },
      }));

      toast({
        title: 'Test Failed',
        description: error.message || 'Test execution failed',
        variant: 'destructive',
      });
    } finally {
      setRunningTests(prev => ({ ...prev, [testId]: false }));
    }
  }, [toast]);

  const handleExport = useCallback((format: 'markdown' | 'csv') => {
    const projectName = project?.project_name || 'Unknown Project';
    
    if (format === 'markdown') {
      const content = generateReviewReport(summary, checklist, projectName);
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName.replace(/\s+/g, '_')}_review_report.md`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const content = generateReviewCSV(summary, checklist);
      const blob = new Blob([content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName.replace(/\s+/g, '_')}_review_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }

    toast({
      title: 'Report Exported',
      description: `Review report exported as ${format.toUpperCase()}`,
    });
  }, [summary, checklist, project, toast]);

  const handleProceed = useCallback(async () => {
    if (!project || !requiredItemsComplete) {
      toast({
        title: 'Requirements Not Met',
        description: 'Please complete all required checklist items before proceeding',
        variant: 'destructive',
      });
      return;
    }

    try {
      await onboardingApi.updateProject(project.id, {
        current_stage: 'go_live',
      });
      await loadProject();
      navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-6`);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to advance stage',
        variant: 'destructive',
      });
    }
  }, [project, requiredItemsComplete, loadProject, navigate, currentWorkspace, projectId, toast]);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-5 w-96" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const renderChecklistItem = (item: ChecklistItem) => (
    <Card 
      key={item.id} 
      className={
        item.status === 'complete' 
          ? 'border-green-200 bg-green-50/50' 
          : item.status === 'warning' 
            ? 'border-amber-200 bg-amber-50/50' 
            : 'border-muted'
      }
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="mt-1">
              {item.status === 'complete' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : item.status === 'warning' ? (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              ) : (
                <XCircle className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">{item.title}</h4>
                {item.required && (
                  <Badge variant="destructive" className="text-xs">
                    Required
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs capitalize">
                  {item.category}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{item.description}</p>
              {item.details && (
                <p className="text-xs text-muted-foreground mt-1 font-medium">{item.details}</p>
              )}
              {item.count !== undefined && item.total !== undefined && item.total > 0 && (
                <Progress 
                  value={(item.count / item.total) * 100} 
                  className="mt-2 h-2"
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {item.status === 'complete' ? (
              <Badge className="bg-green-600">Complete</Badge>
            ) : item.status === 'warning' ? (
              <Badge variant="outline" className="border-amber-600 text-amber-700">
                Warning
              </Badge>
            ) : (
              <Badge variant="secondary">Pending</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Stage 5: Review & Testing</h2>
        <p className="text-muted-foreground mt-1">
          Review configuration and verify system readiness before customer sign-off
        </p>
      </div>

      {/* Overall Status */}
      <Card className={allItemsComplete ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {allItemsComplete ? (
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-amber-600" />
                )}
                <div>
                  <h3 className="text-xl font-semibold">
                    {allItemsComplete ? 'Ready for Sign-Off' : 'Review Required'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {checklist.filter((i) => i.status === 'complete').length} of {checklist.length} items complete
                  </p>
                </div>
              </div>
              <Progress
                value={(checklist.filter((i) => i.status === 'complete').length / checklist.length) * 100}
                className="mt-4"
              />
            </div>
            <div className="text-right ml-8">
              <p className="text-sm text-muted-foreground">Readiness Score</p>
              <p className="text-3xl font-bold">{summary.readinessScore}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collection Sources</p>
                <p className="text-2xl font-bold">{summary.accountsWithCollection}/{summary.totalAccounts}</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>Portal:</span>
                <span className="font-medium">{summary.collectionSources.portal}</span>
              </div>
              <div className="flex justify-between">
                <span>Email:</span>
                <span className="font-medium">{summary.collectionSources.email}</span>
              </div>
              <div className="flex justify-between">
                <span>Manual:</span>
                <span className="font-medium">{summary.collectionSources.manual}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Bot className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Parsing Methods</p>
                <p className="text-2xl font-bold">{summary.accountsWithParsing}/{summary.totalAccounts}</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>Vendor API:</span>
                <span className="font-medium">{summary.parsingMethods.vendor_api}</span>
              </div>
              <div className="flex justify-between">
                <span>AI:</span>
                <span className="font-medium">{summary.parsingMethods.ai}</span>
              </div>
              <div className="flex justify-between">
                <span>Manual:</span>
                <span className="font-medium">{summary.parsingMethods.manual}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Building className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">GL Codes</p>
                <p className="text-2xl font-bold">{summary.accountsWithGLCode}/{summary.totalAccounts}</p>
              </div>
            </div>
            <Progress 
              value={summary.totalAccounts > 0 ? (summary.accountsWithGLCode / summary.totalAccounts) * 100 : 0} 
              className="mt-3 h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <CreditCard className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Terms</p>
                <p className="text-2xl font-bold">{summary.accountsWithPayment}/{summary.totalAccounts}</p>
              </div>
            </div>
            <Progress 
              value={summary.totalAccounts > 0 ? (summary.accountsWithPayment / summary.totalAccounts) * 100 : 0} 
              className="mt-3 h-2"
            />
          </CardContent>
        </Card>
      </div>

      {/* Checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pre-Go-Live Checklist</CardTitle>
              <CardDescription>
                Verify all requirements are met before proceeding to customer sign-off
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('markdown')}>
                <FileText className="h-4 w-4 mr-2" />
                Report
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList>
              <TabsTrigger value="all">All ({checklist.length})</TabsTrigger>
              <TabsTrigger value="data">
                Data ({checklist.filter((i) => i.category === 'data').length})
              </TabsTrigger>
              <TabsTrigger value="configuration">
                Configuration ({checklist.filter((i) => i.category === 'configuration').length})
              </TabsTrigger>
              <TabsTrigger value="quality">
                Quality ({checklist.filter((i) => i.category === 'quality').length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-3 mt-4">
              {checklist.map(renderChecklistItem)}
            </TabsContent>

            {['data', 'configuration', 'quality'].map((category) => (
              <TabsContent key={category} value={category} className="space-y-3 mt-4">
                {checklist
                  .filter((item) => item.category === category)
                  .map(renderChecklistItem)}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Testing Section */}
      <Card>
        <CardHeader>
          <CardTitle>System Tests</CardTitle>
          <CardDescription>
            Run automated tests to verify system functionality before go-live
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: 'data_integrity', icon: Database, title: 'Data Integrity Test', desc: 'Verify data relationships and consistency' },
              { id: 'bill_source', icon: Mail, title: 'Bill Source Test', desc: 'Test bill source connectivity' },
              { id: 'configuration', icon: Settings, title: 'Configuration Test', desc: 'Verify all configurations are valid' },
              { id: 'user_access', icon: Users, title: 'User Access Test', desc: 'Verify user permissions and access' },
            ].map(test => (
              <Card key={test.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <test.icon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h4 className="font-semibold">{test.title}</h4>
                        <p className="text-sm text-muted-foreground">{test.desc}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runTest(test.id)}
                      disabled={runningTests[test.id]}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {runningTests[test.id] ? 'Running...' : 'Run'}
                    </Button>
                  </div>
                  {testResults[test.id] && (
                    <div className={`p-3 rounded-md ${
                      testResults[test.id].status === 'passed' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {testResults[test.id].status === 'passed' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">{testResults[test.id].message}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Accounts</p>
              <p className="text-2xl font-bold">{summary.totalAccounts}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Configured</p>
              <p className="text-2xl font-bold">{summary.accountsWithCollection}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Data Completeness</p>
              <p className="text-2xl font-bold">{summary.dataCompletenessScore}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Readiness</p>
              <p className="text-2xl font-bold">{summary.readinessScore}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-4`)}
        >
          ← Previous Stage
        </Button>
        <Button
          onClick={handleProceed}
          disabled={!requiredItemsComplete}
        >
          Continue to Sign-Off →
        </Button>
      </div>
    </div>
  );
};

export default Stage5Review;
